import React, { useState } from "react";
import { Button, Typography, Box, Divider, Checkbox, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import { CheckCircle, Google, LockOutlined, MailOutline, Microsoft, Visibility, VisibilityOff, Person } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { CustomTextField } from "components";
import { NotistackServices, UserServices } from "shared/services";
import { getVersion, Validate } from "shared/utils";
import { Link } from "components";

export const RegisterPage = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [showSuccessIcon, setShowSuccessIcon] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [acceptTerms, setAcceptTerms] = useState(false);

	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const getPasswordStrength = (pwd: string) => {
		if (!pwd) return { score: 10, label: "Vazia", color: "var(--mui-palette-text-primary)" };
		let score = 0;
		if (pwd.length >= 8) score++;
		if (/[A-Z]/.test(pwd)) score++;
		if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;

		switch (score) {
			case 1:
				return { score: 33, label: "Fraca", color: "var(--mui-palette-error-main)" };
			case 2:
				return { score: 66, label: "Boa", color: "var(--mui-palette-warning-main)" };
			case 3:
				return { score: 100, label: "Forte", color: "var(--mui-palette-primary-main)" };
			default:
				return { score: 10, label: "Muito Fraca", color: "var(--mui-palette-error-main)" };
		}
	};

	const strength = getPasswordStrength(password);

	const handleByProvider = (provider: string) => () => {
		NotistackServices.showInfoMessage(`Login via ${provider} ainda não implementado.`);
	};

	const disabled =
		firstName.trim() === "" ||
		lastName.trim() === "" ||
		email.trim() === "" ||
		!Validate.isValidEmail(email) ||
		password.trim() === "" ||
		strength.score < 50 ||
		confirmPassword.trim() === "" ||
		confirmPassword !== password ||
		!acceptTerms;

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (disabled || loading) return;

		setLoading(true);

		try {
			await UserServices.createUser(
				{
					displayName: `${firstName.trim()} ${lastName.trim()}`,
					email: email,
					password: password,
				},
				true,
			);

			NotistackServices.showSuccessMessage("Conta criada com sucesso!");

			navigate("/", { replace: true });
		} catch (error: any) {
			NotistackServices.showErrorMessage(error?.message || "Erro ao criar conta. Tente novamente.");
			setLoading(false);
		}
	};

	return (
		<div className={styles.loginForm}>
			<form onSubmit={handleSubmit}>
				<CustomTextField
					label="Primeiro Nome *"
					placeholder="Seu nome"
					value={firstName}
					onChange={(e) => setFirstName(e.target.value)}
					error={firstName.trim() === ""}
					autoComplete="given-name"
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Person sx={{ color: "currentColor" }} />
							</InputAdornment>
						),
					}}
				/>
				<CustomTextField
					label="Sobrenome *"
					placeholder="Seu sobrenome"
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
					error={lastName.trim() === ""}
					autoComplete="family-name"
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Person sx={{ color: "currentColor" }} />
							</InputAdornment>
						),
					}}
				/>

				<CustomTextField
					label="E-mail *"
					placeholder="seuemail@exemplo.com"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					error={email.trim() === "" || !Validate.isValidEmail(email)}
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<MailOutline sx={{ color: "currentColor" }} />
							</InputAdornment>
						),
						endAdornment: showSuccessIcon && (
							<InputAdornment position="end">
								<CheckCircle sx={{ color: "var(--mui-palette-primary-main)", fontSize: 20 }} />
							</InputAdornment>
						),
					}}
				/>

				<CustomTextField
					label="Criar Senha *"
					placeholder="••••••••"
					type={showPassword ? "text" : "password"}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					error={password.trim() === "" || strength.score < 50}
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<LockOutlined sx={{ color: "currentColor" }} />
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleClickShowPassword}
									onMouseDown={handleMouseDownPassword}
									edge="end"
									disabled={loading}
									sx={{ "color": "currentColor", "&:hover": { color: "text.primary" } }}
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						),
					}}
				>
					<div className={styles.strengthBar}>
						<div
							className={styles.strengthProgress}
							style={{
								width: `${strength.score}%`,
								backgroundColor: strength.color,
							}}
						></div>
					</div>
					<Typography
						variant="caption"
						sx={{ color: "rgba(var(--mui-palette-text-secondaryChannel) / 0.7)", mt: 1, display: "none", ml: 0.5 }}
					>
						Força da senha: <span style={{ color: strength.color, fontWeight: 600 }}>{strength.label}</span>
					</Typography>

					<div className={styles.requirementsList}>
						<Typography className={styles.requirementsTitle}>Requisitos da senha:</Typography>
						<ul>
							<li className={password.length >= 8 ? styles.valid : ""}>{password.length >= 8 ? "✓" : "○"} Mínimo 8 caracteres</li>
							<li className={/[A-Z]/.test(password) ? styles.valid : ""}>
								{/[A-Z]/.test(password) ? "✓" : "○"} Pelo menos uma letra maiúscula
							</li>
							<li className={/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password) ? styles.valid : ""}>
								{/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password) ? "✓" : "○"} Número ou caractere especial
							</li>
						</ul>
					</div>
				</CustomTextField>

				<CustomTextField
					label="Confirmar Senha *"
					type={showPassword ? "text" : "password"}
					placeholder="••••••••"
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					error={confirmPassword.trim() === "" || confirmPassword !== password}
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<LockOutlined sx={{ color: "currentColor" }} />
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleClickShowPassword}
									onMouseDown={handleMouseDownPassword}
									edge="end"
									disabled={loading}
									sx={{ "color": "currentColor", "&:hover": { color: "text.primary" } }}
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						),
					}}
				/>

				<div className={styles.checkboxContainer}>
					<Checkbox
						sx={{
							"color": "rgba(var(--mui-palette-text-secondaryChannel) / 0.2)",
							"p": 0,
							"&.Mui-checked": { color: "var(--mui-palette-primary-main)" },
						}}
						checked={acceptTerms}
						onChange={(e) => setAcceptTerms(e.target.checked)}
						disabled={loading}
					/>
					<Typography className={styles.checkboxLabel}>
						Li e aceito os <Link href="#">Termos de Uso</Link> e a <Link href="#">Política de Privacidade</Link>.
					</Typography>
				</div>

				<Button
					fullWidth
					variant="contained"
					className={styles.submitButton}
					sx={{ mt: 2, py: 1.8 }}
					disabled={disabled || loading}
					type="submit"
				>
					{loading ? (
						<CircularProgress
							size={24}
							color="inherit"
						/>
					) : (
						"Criar Minha Conta"
					)}
				</Button>
			</form>

			<div className={styles.dividerContainer}>
				<Divider
					sx={{
						"&::before, &::after": { borderColor: "rgba(var(--mui-palette-text-primaryChannel) / 0.1)" },
					}}
				>
					<Typography
						variant="caption"
						className={styles.dividerText}
						sx={{ px: 2, color: "rgba(var(--mui-palette-text-secondaryChannel) / 0.6)", fontWeight: 700 }}
					>
						Ou continue com
					</Typography>
				</Divider>
			</div>

			<div className={styles.socialButtonsGrid}>
				<Button
					fullWidth
					variant="outlined"
					startIcon={<Google />}
					className={styles.socialButton}
					onClick={handleByProvider("google")}
				>
					Google
				</Button>
				{/* <Button
					fullWidth
					variant="outlined"
					startIcon={<Microsoft />}
					className={styles.socialButton}
				>
					Azure
				</Button> */}
			</div>

			<div className={styles.formFooter}>
				<Typography variant="body2">
					Já tem uma conta?
					<Link
						href="./login"
						className={styles.loginLink}
						preserveQueryParams
					>
						Entrar
					</Link>
				</Typography>
			</div>

			<div className={styles.footerVersion}>{getVersion()}</div>
		</div>
	);
};
